<template>
  <div class="overlay-loading" v-show="isWaitingForServer"></div>
  <div class="lotto-actions">
    <h2>Lotto</h2>
    <ul class="list-buttons">
      <li v-for="game in games" v-bind:key="game.id">
        <button
          class="button"
          @click.stop="chooseGame(game)"
          :class="
            chosenGame != null && game.id == chosenGame.id
              ? 'disabled'
              : 'enabled'
          "
        >
          {{ game.name }} - {{ formatPrice(game) }} SEK
        </button>
      </li>
    </ul>

    <div class="ticket-buy" v-if="chosenGame != null">
      <p>
        Buy a ticket for <strong>{{ chosenGame.name }}</strong>
        <span v-if="lottoMeta">
          &nbsp;—&nbsp;Price: {{ lottoMeta.price }} SEK &nbsp;—&nbsp;Status:
          {{ lottoMeta.status }}
          <span v-if="lottoMeta.nextRoundDrawDate"
            >&nbsp;—&nbsp;Next draw:
            {{ formatDate(lottoMeta.nextRoundDrawDate) }}</span
          >
          <span v-if="lottoMeta.currentJackpotBalance != null"
            >&nbsp;—&nbsp;Jackpot:
            {{ lottoMeta.currentJackpotBalance }} SEK</span
          >
        </span>
      </p>

      <div class="number-section" v-if="lottoMeta">
        <p>
          Choose {{ lottoMeta.numbersToChoose }} numbers ({{ lottoMeta.numberRangeMin }} - {{ lottoMeta.numberRangeMax }}):
        </p>
        <div class="number-grid">
          <button
            v-for="n in numberRangeFromMeta"
            :key="n"
            :class="{ selected: chosenNumbers.includes(n) }"
            @click="toggleNumber(n)"
          >
            {{ n }}
          </button>
        </div>
        <div>Selected: {{ chosenNumbers.join(", ") }}</div>
        <button class="button" @click="pickAutoNumbers()">Random</button>
      </div>
      <div
        v-if="
          lottoMeta &&
          lottoMeta.extraNumbersToChoose &&
          lottoMeta.extraNumbersToChoose > 0
        "
        class="number-section"
        style="margin-top: 8px"
      >
        <p>
          Choose {{ lottoMeta.extraNumbersToChoose }} extra numbers ({{
            lottoMeta.extraNumberRangeMin
          }}
          - {{ lottoMeta.extraNumberRangeMax }}):
        </p>
        <div class="number-grid">
          <button
            v-for="n in extraNumberRangeFromMeta"
            :key="'extra-' + n"
            :class="{ selected: chosenExtraNumbers.includes(n) }"
            @click="toggleExtraNumber(n)"
          >
            {{ n }}
          </button>
        </div>
        <div>Selected extra: {{ chosenExtraNumbers.join(", ") }}</div>
        <button class="button" @click="pickAutoExtraNumbers()">Random</button>
      </div>

      <div style="margin-top: 8px">
        <label>Round (optional):</label>
        <select
          v-model.number="selectedRoundId"
          style="min-width: 420px; margin-left: 6px"
        >
          <option :value="0">None (use default/open)</option>
          <option
            v-for="r in rounds"
            :key="r.roundId || r.id || r.RoundId"
            :value="r.roundId || r.id || r.RoundId"
          >
            {{ formatRoundLabel(r) }}
          </option>
        </select>
      </div>

      <div style="margin-top: 8px">
        <button class="button" @click="buy()" :disabled="!canBuy" :title="buyDisabledReason">
          Buy ticket
        </button>
        <div class="validation-message" v-if="!canBuy" style="margin-top:6px;color:#b00">
          <small>{{ buyDisabledReason }}</small>
        </div>
      </div>
    </div>

    <div class="unfinished" v-if="unfinishedTickets.length > 0">
      <h2>My purchased / unfinished tickets</h2>
      <ul class="unfinished-list">
        <li
          v-for="ticket in unfinishedTickets"
          v-bind:key="ticket.correlationId"
        >
          ID {{ ticket.correlationId }} - Game: {{ ticket.gameId }} -
          <a href="#" @click.stop="finalizeTicket(ticket)">Try finalize</a>
        </li>
      </ul>
    </div>

    <div
      class="latest-tickets"
      v-if="latestTickets && latestTickets.roundTickets && latestTickets.roundTickets.length > 0">
      <h2>My latest purchased tickets and results</h2>
      <ul class="latest-list">
        <li v-for="rt in latestTickets.roundTickets" :key="rt.roundId">
          <div>
            <strong>Round:</strong> {{ rt.roundId }} | <strong>Date:</strong>
            {{ rt.roundDate }}
          </div>
          <div v-for="t in rt.tickets" :key="t.ticketId">
            Ticket ID: {{ t.ticketId }} | Numbers: {{ t.numbers.join(", ") }}
            <span v-if="t.prizeValue != null">
              | Prize: {{ t.prizeValue }} SEK</span
            >
            <span v-if="t.drawNumbers">
              | Drawn: {{ t.drawNumbers.join(", ") }}</span
            >
          </div>
        </li>
      </ul>
    </div>

    <div class="ticket-result" v-if="lastTicketResult">
      <h2>Ticket result</h2>
      <div>
        Ticket ID:
        {{ lastTicketResult.id || lastTicketResult.ticketIdentifier }}
      </div>
      <div>
        Prize:
        {{ lastTicketResult.prize || lastTicketResult.prizeValue || 0 }} SEK
      </div>
      <div v-if="lastTicketResult.numbers">
        Drawn numbers: {{ lastTicketResult.numbers.join(", ") }}
      </div>
    </div>
  </div>
</template>

<script>
import store from "../store.js";
import {
  updateBalance,
  getUnfinishedTickets,
  finalizeGameWithCorrelationId,
  getLottoTicket,
  getLottoRounds,
  getLottoGame,
  getLatestLottoTickets
} from "../api.js";
import { unifiedBuy } from '../utils/unifiedBuy.js';
import { formatPrice } from "../utils/gameHelper.js";

export default {
  name: "LottoGame",
    mounted() {
      const cfg = store.clientConfig;
      this.games = cfg && cfg['Lotto'] ? (cfg['Lotto'] || []) : [];
    },
  data() {
    return {
      isWaitingForServer: false,
      chosenGame: null,
      store: store,
      unfinishedTickets: [],
      lastTicketResult: null,
      latestTickets: null,
      games: [],
      manualSelection: false,
      chosenNumbers: [],
      chosenExtraNumbers: [],
      selectedRoundId: 0,
      rounds: [],
      lottoMeta: null,
    };
  },
  methods: {
    formatPrice,
    formatRoundLabel(r) {
      if (!r) return "";
      const id = r.roundId;
      const draw = r.drawTime;
      const cutoff = r.cutOffTime;
      const fmt = (s) => {
        if (!s) return "";
        try {
          const d = new Date(s);
          if (!isNaN(d)) return d.toLocaleString();
        } catch (e) {
          /* ignore */
        }
        return s;
      };
      const drawStr = draw ? `draw: ${fmt(draw)}` : "";
      const cutStr = cutoff ? `cutoff: ${fmt(cutoff)}` : "";
      return [id, drawStr, cutStr].filter(Boolean).join(" | ");
    },
    formatDate(s) {
      if (!s) return "";
      try {
        const d = new Date(s);
        if (!isNaN(d)) return d.toLocaleString();
      } catch (e) {
        /* ignore */
      }
      return s;
    },
    chooseGame(_game) {
      this.chosenGame = _game;
      this.lottoMeta = null;
      this.lastTicketResult = null;
      this.checkUnfinishedTickets();
      this.fetchLatestTickets();
      // reset selections
      this.chosenNumbers = [];
      this.chosenExtraNumbers = [];
      this.manualSelection = false;
      // try to load rounds and default selection
      const lottoId = this.chosenGame && this.chosenGame.buyTicketMeta && this.chosenGame.buyTicketMeta.gameId ? this.chosenGame.buyTicketMeta.gameId : null;
      if (lottoId) {
        console.log('Lotto.chooseGame: lottoId=', lottoId, 'chosenGame.buyTicketMeta=', this.chosenGame && this.chosenGame.buyTicketMeta);
        getLottoGame(lottoId)
          .then((g) => {
            console.log('Lotto.chooseGame: getLottoGame response:', g);
            this.lottoMeta = g;
          })
          .catch((err) => {
            console.error('Lotto.chooseGame: getLottoGame failed:', err);
          });

        // then fetch open rounds
        getLottoRounds(lottoId)
          .then((data) => {
            if (!data) return;
            // normalize to array
            const list = Array.isArray(data) ? data : [data];
            this.rounds = list;
            if (list.length === 0) return;
            const chosen =
              list.find(
                (r) =>
                  r.status === 1 || r.status === "Open" || r.status === "open",
              ) || list[0];
            if (chosen) this.selectedRoundId = chosen.roundId;
          })
          .catch(() => {
            /* ignore */
          })
          // fetch latest tickets for this lotto
          this.fetchLatestTickets(lottoId);
      }
    },
    async fetchLatestTickets() {
      try {
        this.latestTickets = await getLatestLottoTickets();
      } catch {
        this.latestTickets = null;
      }
    },
    checkUnfinishedTickets() {
      if (!this.chosenGame) return;
      getUnfinishedTickets(this.chosenGame.id)
        .then((data) => {
          if (!data) this.unfinishedTickets = [];
          else this.unfinishedTickets = data;
        })
        .catch(() => {
          this.unfinishedTickets = [];
        });
    },
    pickAutoNumbers() {
      if (!this.lottoMeta) return;
      const picks = new Set();
      const target = this.lottoMeta.numbersToChoose;
      const min = this.lottoMeta.numberRangeMin;
      const max = this.lottoMeta.numberRangeMax;
      while (picks.size < target) {
        const n = Math.floor(Math.random() * (max - min + 1)) + min;
        picks.add(n);
      }
      this.chosenNumbers = Array.from(picks).sort((a, b) => a - b);
    },
    pickAutoExtraNumbers() {
      if (!this.lottoMeta) return;
      const extras = new Set();
      const target = this.lottoMeta.extraNumbersToChoose;
      const min = this.lottoMeta.extraNumberRangeMin;
      const max = this.lottoMeta.extraNumberRangeMax;
      while (extras.size < target) {
        const m = Math.floor(Math.random() * (max - min + 1)) + min;
        extras.add(m);
      }
      this.chosenExtraNumbers = Array.from(extras).sort((a, b) => a - b);
    },
    toggleNumber(n) {
      const idx = this.chosenNumbers.indexOf(n);
      if (idx === -1) {
        if (!this.lottoMeta) return;
        if (this.chosenNumbers.length < this.lottoMeta.numbersToChoose) {
          this.chosenNumbers.push(n);
          this.chosenNumbers.sort((a, b) => a - b);
        }
      } else {
        this.chosenNumbers.splice(idx, 1);
      }
    },
    toggleExtraNumber(n) {
      const idx = this.chosenExtraNumbers.indexOf(n);
      if (idx === -1) {
        if (!this.lottoMeta) return;
        if (
          this.chosenExtraNumbers.length < this.lottoMeta.extraNumbersToChoose
        ) {
          this.chosenExtraNumbers.push(n);
          this.chosenExtraNumbers.sort((a, b) => a - b);
        }
      } else {
        this.chosenExtraNumbers.splice(idx, 1);
      }
    },
    buy() {
      if (!this.chosenGame) return false;
      // ensure we have chosen numbers
      if (!this.lottoMeta) {
        alert("Lotto metadata not loaded.");
        return;
      }
      if (
        !this.chosenNumbers ||
        this.chosenNumbers.length !== this.lottoMeta.numbersToChoose
      ) {
        alert(
          "Please choose " +
            this.lottoMeta.numbersToChoose +
            " numbers before buying (or use Auto).",
        );
        return;
      }

      // if extra numbers are required, ensure they are chosen
      if (
        this.lottoMeta.extraNumbersToChoose &&
        this.lottoMeta.extraNumbersToChoose > 0
      ) {
        if (
          !this.chosenExtraNumbers ||
          this.chosenExtraNumbers.length !== this.lottoMeta.extraNumbersToChoose
        ) {
          alert(
            "Please choose " +
              this.lottoMeta.extraNumbersToChoose +
              " extra numbers.",
          );
          return;
        }
      }

      // validate duplicates
      const uniq = new Set(this.chosenNumbers);
      if (uniq.size !== this.chosenNumbers.length) {
        alert("Please remove duplicate numbers.");
        return;
      }

      // validate range
      for (const n of this.chosenNumbers) {
        if (
          n < this.lottoMeta.numberRangeMin ||
          n > this.lottoMeta.numberRangeMax
        ) {
          alert(
            "Number " +
              n +
              " is out of range (" +
              this.lottoMeta.numberRangeMin +
              "-" +
              this.lottoMeta.numberRangeMax +
              ").",
          );
          return;
        }
      }

      // validate extra numbers
      if (
        this.lottoMeta.extraNumbersToChoose &&
        this.lottoMeta.extraNumbersToChoose > 0
      ) {
        const uniqExtra = new Set(this.chosenExtraNumbers);
        if (uniqExtra.size !== this.chosenExtraNumbers.length) {
          alert("Please remove duplicate extra numbers.");
          return;
        }
        for (const m of this.chosenExtraNumbers) {
          if (
            m < this.lottoMeta.extraNumberRangeMin ||
            m > this.lottoMeta.extraNumberRangeMax
          ) {
            alert(
              "Extra number " +
                m +
                " is out of range (" +
                this.lottoMeta.extraNumberRangeMin +
                "-" +
                this.lottoMeta.extraNumberRangeMax +
                ").",
            );
            return;
          }
        }
      }

      this.isWaitingForServer = true;

      unifiedBuy({
        game: this.chosenGame,
        buyOverrides: {
          roundId: this.selectedRoundId || 0,
          chosenNumbers: this.chosenNumbers,
          chosenExtraNumbers: this.chosenExtraNumbers || []
        },
        setLoading: (v) => { this.isWaitingForServer = v; },
        refreshTickets: () => this.checkUnfinishedTickets(),
        refreshBalance: () => updateBalance(),
        onError: (err) => {
          this.isWaitingForServer = false;
          let msg =
            err && err.data && err.data.data
              ? err.data.data
              : err && err.data
              ? err.data
              : err && err.statusText
              ? err.statusText
              : "Buy failed";
          alert("Buy failed: " + msg);
          console.dir(err);
        }
      });
    },
    async finalizeTicket(_ticket) {
      if (!_ticket || !_ticket.correlationId) return;
      this.isWaitingForServer = true;
      finalizeGameWithCorrelationId(_ticket.correlationId)
        .then(async (data) => {
          if (!data || !data.tickets) {
            this.isWaitingForServer = false;
            return;
          }
          for (const t of data.tickets) {
            let ticket = t;
            if (ticket && ticket.id) {
              const full = await getLottoTicket(ticket.id);
              if (full) ticket = full;
            }
            this.lastTicketResult = ticket;
          }
          this.isWaitingForServer = false;
          this.checkUnfinishedTickets();
          updateBalance();
        })
        .catch(() => {
          this.isWaitingForServer = false;
        });
    },
  },
  computed: {
    numberRangeFromMeta() {
      const arr = [];
      if (!this.lottoMeta) return arr;
      const min = this.lottoMeta.numberRangeMin || 1;
      const max = this.lottoMeta.numberRangeMax || min;
      for (let i = min; i <= max; i++) arr.push(i);
      return arr;
    },
    extraNumberRangeFromMeta() {
      const arr = [];
      if (!this.lottoMeta) return arr;
      const min = this.lottoMeta.extraNumberRangeMin || 1;
      const max = this.lottoMeta.extraNumberRangeMax || min;
      for (let i = min; i <= max; i++) arr.push(i);
      return arr;
    },
    canBuy() {
      if (!this.lottoMeta) return false;
      if (this.isWaitingForServer) return false;
      if (!this.selectedRoundId || this.selectedRoundId === 0) return false;
      if (!this.chosenNumbers || this.chosenNumbers.length !== (this.lottoMeta ? this.lottoMeta.numbersToChoose : 0)) return false;
      if (this.lottoMeta && this.lottoMeta.extraNumbersToChoose && this.lottoMeta.extraNumbersToChoose > 0) {
        if (!this.chosenExtraNumbers || this.chosenExtraNumbers.length !== this.lottoMeta.extraNumbersToChoose) return false;
      }
      return true;
    },
    buyDisabledReason() {
      const reasons = [];
      if (!this.lottoMeta) reasons.push('Laddar lotto-metadata...');
      if (!this.selectedRoundId || this.selectedRoundId === 0) reasons.push('Välj en runda i dropdown innan köp.');
      if (!this.chosenNumbers || (this.lottoMeta && this.chosenNumbers.length !== this.lottoMeta.numbersToChoose)) {
        const need = this.lottoMeta ? this.lottoMeta.numbersToChoose : 'rätt antal';
        reasons.push(`Välj ${need} nummer.`);
      }
      if (this.lottoMeta && this.lottoMeta.extraNumbersToChoose && this.lottoMeta.extraNumbersToChoose > 0) {
        if (!this.chosenExtraNumbers || this.chosenExtraNumbers.length !== this.lottoMeta.extraNumbersToChoose) {
          reasons.push(`Välj ${this.lottoMeta.extraNumbersToChoose} extranummer.`);
        }
      }
      return reasons.join(' ');
    }
  },
};
</script>

<style scoped>
ul.list-buttons {
  list-style-type: none;
  padding: 0;
}
ul.list-buttons li {
  display: inline-block;
  margin: 0 10px 10px 0;
}
.number-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  max-width: 420px;
  margin-bottom: 8px;
}
.number-grid button {
  padding: 6px 8px;
  font-size: 0.9em;
}
.number-grid button.selected {
  background: #dbb230;
  color: #fff;
}
</style>
